<template>
  <div class="approve-contact-page">
    <h2>{{ $t('W2P_BILLING_ADDRESS') }}</h2>
    <div class="form-container">
      <pn-select
        v-if="isBusinessUser"
        name="customerNumber"
        :invalid="invalidInvoicePayment"
        :placeholder="
          getTranslation(
            customers.length > 0 || fetchingCustomers
              ? 'W2P_SELECT_CUSTOMER'
              : 'POSTNORD_NO_VALID_CUSTOMER_NUMBER'
          )
        "
        @selectsearch="searchCustomer"
        :searchable="customers.length > 0"
        searchPlaceholder="getTranslation('W2P_SEARCH_CUSTOMER')"
        :loading="fetchingCustomers && isBusinessUser"
      >
        <pn-option
          :value="customer.customerNumber"
          :selected="isSelected(customer)"
          :key="i"
          v-show="customer.show"
          v-for="(customer, i) in filteredCustomers"
          @click="selectCustomer(customer)"
        >
          {{ customer.customerNumber + ' ' + customer.customerNumberName }}
        </pn-option>
      </pn-select>
      <div v-show="invalidInvoicePayment" class="custom-validation-text">
        {{ $t('W2P_INVOICE_PAYMENT_ERROR') }}
      </div>
      <pn-input-container
        :invalid="errors.has('company')"
        :noicon="errors.has('company')"
        :valid="company.valid"
        v-if="isBusinessUser"
        :label="$t('address.company')"
      >
        <input
          v-validate="'required|max:35'"
          name="company"
          v-model="payForm.companyName"
          :aria-label="$t('address.company')"
        />
        <div v-show="errors.has('company')" class="validation-text">
          {{ $t(errors.first('company')) }}
        </div>
      </pn-input-container>
      <pn-input-container
        :invalid="errors.has('organizationNumber')"
        :noicon="errors.has('organizationNumber')"
        :valid="organizationNumber.valid"
        v-if="isBusinessUser"
        :label="$t('ORGANIZATION_NUMBER')"
      >
        <input
          v-validate="'required|max:35'"
          name="organizationNumber"
          v-model="payForm.organizationNumber"
          :aria-label="$t('ORGANIZATION_NUMBER')"
        />
        <div v-show="errors.has('organizationNumber')" class="validation-text">
          {{ $t(errors.first('organizationNumber')) }}
        </div>
      </pn-input-container>
      <pn-input-container
        v-if="!isBusinessUser"
        :valid="fullName.valid"
        :invalid="errors.has('fullName')"
        :noicon="errors.has('fullName')"
        :label="$t('FULL_NAME')"
      >
        <input
          v-validate="'required|max:50'"
          name="fullName"
          v-model="payForm.fullName"
          :aria-label="$t('FULL_NAME')"
        />
        <div v-show="errors.has('fullName')" class="validation-text">
          {{ $t(errors.first('fullName')) }}
        </div>
      </pn-input-container>
      <pn-input-container
        :valid="address.valid"
        :invalid="errors.has('address')"
        :noicon="errors.has('address')"
        :label="$t('ADDRESS')"
      >
        <input
          v-validate="'required|max:35'"
          name="address"
          v-model="payForm.address"
          :aria-label="$t('ADDRESS')"
        />
        <div v-show="errors.has('address')" class="validation-text">
          {{ $t(errors.first('address')) }}
        </div>
      </pn-input-container>

      <country-selection
        :searchable="true"
        :value="payForm.country"
        v-model="payForm.country"
      ></country-selection>

      <pn-input-container
        :valid="postalCode.valid"
        :invalid="errors.has('postalCode')"
        :noicon="errors.has('postalCode')"
        :label="$t('POSTAL_CODE')"
      >
        <input
          v-validate="`required|postalCode:${payForm.country}`"
          name="postalCode"
          v-model="payForm.postalCode"
          :aria-label="$t('POSTAL_CODE')"
          @blur="fetchCity()"
        />
        <div v-show="errors.has('postalCode')" class="validation-text">
          {{ $t(errors.first('postalCode')) }}
        </div>
      </pn-input-container>
      <pn-input-container
        :valid="city.valid"
        :invalid="errors.has('city')"
        :noicon="errors.has('city')"
        :label="$t('CITY')"
        :loading="loadingCity"
      >
        <input
          v-validate="'required|alpha_spaces'"
          name="city"
          v-model="payForm.city"
          :aria-label="$t('CITY')"
        />
        <div v-show="errors.has('city')" class="validation-text">
          {{ $t(errors.first('city')) }}
        </div>
      </pn-input-container>
      <pn-input-container
        :valid="contactPerson.valid"
        v-if="isBusinessUser"
        :invalid="errors.has('contactPerson')"
        :noicon="errors.has('contactPerson')"
        :label="$t('CONTACT_PERSON')"
      >
        <input
          v-validate="'required|max:35'"
          name="contactPerson"
          v-model="payForm.contactPerson"
          :aria-label="$t('CONTACT_PERSON')"
        />
        <div v-show="errors.has('contactPerson')" class="validation-text">
          {{ $t(errors.first('contactPerson')) }}
        </div>
      </pn-input-container>

      <pn-input-container
        :valid="email.valid"
        :invalid="errors.has('email')"
        :noicon="errors.has('email')"
        :label="$t('EMAIL')"
      >
        <input
          v-validate="'required|email'"
          name="email"
          v-model="payForm.email"
          :aria-label="$t('EMAIL')"
        />
        <div v-show="errors.has('email')" class="validation-text">
          {{ $t(errors.first('email')) }}
        </div>
      </pn-input-container>

      <div class="form-2-col">
        <custom-select-component
          class="country-code"
          label="Country code"
          :values="countryCodes"
          v-model="selectedCountryCode"
          name="countryCode"
        ></custom-select-component>

        <pn-input-container
          :invalid="errors.has('mobile')"
          :noicon="errors.has('mobile')"
          :label="$t('W2P_MOBILE_OPTIONAL')"
        >
          <input
            v-validate="
              selectedCountryCode !== '-1'
                ? `numeric|min:8|max:${maxMobileCharacters()}`
                : `mobile`
            "
            name="mobile"
            v-model="payForm.mobileUnformatted"
            :aria-label="$t('W2P_MOBILE_OPTIONAL')"
          />
          <div v-show="errors.has('mobile')" class="validation-text">
            {{ $t(errors.first('mobile')) }}
          </div>
        </pn-input-container>
      </div>
      <div class="store-option">
        <pn-checkbox-container
          tabIndex="0"
          class="store-checkbox"
          :label="$t('W2P_STORE_BILLING_ON_DEVICE')"
          @checkchange="checkBoxChangeSaveBilling"
        >
          <input
            type="checkbox"
            name="storeCheckbox"
            :aria-label="$t('W2P_STORE_BILLING_ON_DEVICE')"
            v-model="saveDeviceToggle"
          />
        </pn-checkbox-container>
        <p class="small" v-html="$t('W2P_ACCEPTING_POLICY_WARNING')"></p>
      </div>
    </div>

    <payment-method v-if="isBusinessUser"></payment-method>

    <div class="no-border-checkbox" v-if="termsAndConditions">
      <pn-checkbox-container
        tabIndex="0"
        class="terms-conditions-checkbox"
        @click="validatePayForm()"
        @checkchange="checkBoxChange"
      >
        <input type="checkbox" name="termsAndConditions" id="agreeCheckbox" />
        <label for="agreeCheckbox">
          {{ $t('PAYMENTS_PAGE_VERIFY_TERMS') }}
          <a
            v-bind:href="
              !termsAndConditions.generalConditions[language]
                ? termsAndConditions.generalConditions['sv']
                : termsAndConditions.generalConditions[language]
            "
            target="_blank"
            >{{ $t('PAYMENTS_PAGE_GENERAL_CONDITIONS') }}</a
          >
          <span v-if="languageExclusive('SE')">
            {{ $t('PAYMENTS_PAGE_SWEDISH_ONLY') }}</span
          >
          <span v-if="languageExclusive('DK')">
            {{ $t('PAYMENTS_PAGE_DANISH_ONLY') }}</span
          >,
          <a v-bind:href="termsAndConditions.productTerms.url">{{
            $t('PAYMENTS_PAGE_TERMS_OF_SERVICES')
          }}</a>
          <span v-if="languageExclusive('SE')">
            {{ $t('PAYMENTS_PAGE_SWEDISH_ONLY') }}</span
          >
          <span v-if="languageExclusive('DK')">
            {{ $t('PAYMENTS_PAGE_DANISH_ONLY') }}</span
          >
          {{ $t('PAYMENTS_PAGE_TERMS_AND') }}
          <a
            v-bind:href="
              !termsAndConditions.termsOfOrders[language]
                ? termsAndConditions.termsOfOrders['sv']
                : termsAndConditions.termsOfOrders[language]
            "
            target="_blank"
            >{{ $t('PAYMENTS_PAGE_TERMS_OF_ORDER') }}</a
          >
          <span v-if="languageExclusive('SE')">
            {{ $t('PAYMENTS_PAGE_SWEDISH_ONLY') }}</span
          >
          <span v-if="languageExclusive('DK')">
            {{ $t('PAYMENTS_PAGE_DANISH_ONLY') }}</span
          >.
        </label>
      </pn-checkbox-container>
    </div>

    <div class="button-container">
      <pn-button-container secondary size="medium">
        <button @click="back">{{ $t('W2P_LETTER_BACK') }}</button>
      </pn-button-container>

      <pn-button-container size="medium">
        <button
          v-if="isReadOnlyMode"
          :disabled="isReadOnlyMode"
          class="read-only"
        >
          {{ $t('W2P_PAY') }}
        </button>
        <button v-else :disabled="isFormInvalid" class="next" @click="submit()">
          {{ $t('W2P_PAY') }}
        </button>
        <p v-if="isReadOnlyMode" class="read-only-notice">
          {{ $t('YOU_ARE_IN_READ_ONLY_MODE') }}
        </p>
      </pn-button-container>
    </div>
  </div>
</template>

<script>
import PaymentMethod from '@/components/pay/letter/PaymentMethod.vue'
import CustomSelectComponent from '@/components/common/CustomSelectComponent.vue'
import CountrySelection from '@/components/common/country/CountryComponent'

import { mapFields } from 'vee-validate'
import { mapState, mapGetters, mapMutations } from 'vuex'
import axios from 'axios'

export default {
  data: () => ({
    payForm: {},
    selectedCountryCode: '46',
    saveDeviceToggle: false,
    fetchingCustomers: true,
    customers: [],
    fetchingCustomersErr: false,
    searchModel: '',
    selectedCustomer: '',
    termsAndConditions: null,
    agreeWithTC: false,
    loadingCity: false,
    countryCodes: [
      { label: '🇩🇰 +45', value: '45', country: 'DK', max: 15 },
      { label: '🇸🇪 +46', value: '46', country: 'SE', max: 15 },
      { label: '🇳🇴 +47', value: '47', country: 'NO', max: 15 },
      { label: '🇫🇮 +358', value: '358', country: 'FI', max: 16 },
      { label: 'Other', value: '-1', max: 16 }
    ]
  }),
  mounted() {
    if (this.saveDeviceToggle && this.contactInformation) {
      this.payForm = this.contactInformation
    }
  },
  computed: {
    ...mapState('cart', ['productDelivery']),
    ...mapState('letter/pay', [
      'contactInformation',
      'paymentMethod',
      'saveToDevice',
      'orderActivated',
      'paymentStatus'
    ]),
    ...mapFields([
      'address',
      'postalCode',
      'city',
      'fullName',
      'email',
      'contactPerson',
      'mobile',
      'company',
      'customerNumber',
      'organizationNumber'
    ]),
    ...mapGetters(['isBusinessUser', 'isReadOnlyMode']),
    filteredCustomers() {
      const searchRegex = new RegExp(
        (this.searchModel || '').toLowerCase(),
        'i'
      )

      return this.customers.map(item => {
        const displayedText = (
          item.customerNumber +
          ' ' +
          item.customerNumberName
        ).toLowerCase()

        item.show = displayedText.match(searchRegex)

        return item
      })
    },
    isFormInvalid() {
      let fieldsKeys = Object.keys(this.fields)
      let companyFields = [
        'company',
        'contactPerson',
        'customerNumber',
        'organizationNumber'
      ]

      if (!this.isBusinessUser) {
        fieldsKeys = fieldsKeys.filter(item => {
          return companyFields.indexOf(item) < 0
        })
      }

      const fieldValid = fieldsKeys.every(field => {
        return this.fields[field] && this.fields[field].valid
      })

      return (
        this.errors.count() > 0 ||
        !fieldValid ||
        (this.isBusinessUser && this.paymentMethod === 'none') ||
        this.invalidInvoicePayment ||
        !this.agreeWithTC
      )
    },
    invalidInvoicePayment() {
      const { customer } = this.contactInformation
      return (
        this.paymentMethod === 'invoice' &&
        (!customer || (customer && !customer.customerNumber))
      )
    },
    customerSelectionPlaceholder() {
      if (this.customers.length) {
        return this.$t('W2P_SELECT_CUSTOMER')
      }

      return this.fetchingCustomersErr
        ? this.$t('W2P_FETCH_CUSTOMER_NUMBER_FAILED')
        : this.$t('W2P_NO_VALID_CUSTOMER_NUMBER')
    }
  },
  components: {
    PaymentMethod,
    CustomSelectComponent,
    CountrySelection
  },

  beforeCreate() {
    const reqDataTermsAndCondtions = {
      params: {
        productId: this.$store.state.letter.upload.productId,
        userType: this.$store.state.userType,
        addressType: this.$store.state.letter.address.addressType, // fixing this to letter for now. I am not sure if were going to reuse this component
        deliveryId: this.$store.state.letter.upload.deliveryId
      }
    }

    axios
      .get(
        process.env.VUE_APP_W2P_API_BASE_URL +
          '/clientAPI/products/termsAndConditions',
        reqDataTermsAndCondtions
      )
      .then(res => {
        this.termsAndConditions = res.data
      })
      .catch(err => {})

    if (this.$store.getters.isBusinessUser) {
      axios
        .get(process.env.VUE_APP_W2P_API_BASE_URL + '/orders/customers', {
          params: { unique: true },
          headers: {
            Authorization: this.$store.state.accessToken
          }
        })
        .then(res => {
          this.customers = this.filterCustomers(res.data)
          this.fetchingCustomers = false
        })
        .catch(() => {
          this.fetchingCustomersErr = true
          this.fetchingCustomers = false
        })
    }
  },

  beforeMount() {
    this.saveDeviceToggle = this.saveToDevice
    this.language = this.$store.state.language
    this.market = 'SE'

    if (!this.isBusinessUser) {
      this.SET_PAYMENT_METHOD('CARD')
    }
  },

  methods: {
    ...mapMutations('letter/pay', [
      'SAVE',
      'TOGGLE_SAVE_TO_DEVICE',
      'TOGGLE_REFERENCE_SAMPLE',
      'SET_PAYMENT_METHOD'
    ]),
    checkBoxChange(e) {
      this.agreeWithTC = e.detail
    },
    getCountryObject() {
      return this.countryCodes.find(country => {
        return country.value == this.selectedCountryCode
      })
    },
    maxMobileCharacters() {
      const country = this.getCountryObject()
      return country.max
    },
    getCountry() {
      const country = this.getCountryObject()
      return country.country
    },
    formatPhoneNumber() {
      let formatted = ''
      if (this.payForm.mobileUnformatted) {
        if (this.selectedCountryCode === '-1') {
          formatted = this.payForm.mobileUnformatted
        } else {
          formatted = `+${this.selectedCountryCode}${this.payForm.mobileUnformatted}`
        }
      }
      return formatted
    },
    validatePayForm() {
      this.$validator.validate()
    },
    back() {
      this.$router.push({ name: 'pay' })
    },

    getTranslation(key) {
      return this.$t(key)
    },

    checkBoxChangeSaveBilling(e) {
      this.saveDeviceToggle = e.detail
      this.toggleSaveToDevice(this.saveDeviceToggle)
    },
    toggleSaveToDevice() {
      this.TOGGLE_SAVE_TO_DEVICE()
    },
    searchCustomer({ detail }) {
      this.searchModel = detail
    },

    selectCustomer(customer) {
      this.payForm.customer = customer
      this.payForm.companyName = customer.legalName
      this.payForm.postalCode = customer.postalAddress.postalCode
      this.payForm.city = customer.postalAddress.cityName[0].value
      this.payForm.organizationNumber = customer.customerNumber

      const postbox = this.$t('PAYMENTS_PAGE_POSTBOX')
      const poValue = customer.postalAddress.pobox
      const streetName = customer.postalAddress.streetName || ''
      const streetNumber = customer.postalAddress.streetNumber || ''
      this.payForm.address = (streetName + ' ' + streetNumber).trim()

      if ((!streetName || streetName === '') && !!poValue && poValue !== '') {
        this.payForm.address = postbox + ' ' + poValue
      }

      this.SAVE(this.payForm)
    },

    isSelected(customer) {
      return (
        (this.payForm.customer || {}).customerNumber === customer.customerNumber
      )
    },

    languageExclusive(market) {
      const displayConditions = {
        SE:
          this.language !== 'sv' &&
          !this.termsAndConditions.termsOfServices[this.language],
        DK:
          this.language !== 'da' &&
          !this.termsAndConditions.termsOfServices[this.language]
      }

      return displayConditions[market] && this.market === market
    },
    filterCustomers(customers) {
      return customers.filter(customer => {
        return (customer.validSalesOrganizations || []).includes(
          this.productDelivery.salesOrganization || ''
        )
      })
    },
    fetchCity() {
      if (!this.postalCode.valid) {
        return
      }
      this.loadingCity = true

      axios({
        method: 'get',
        url: `${process.env.VUE_APP_SUBSCRIPTIONS_API_BASE}/search/city/${this.payForm.country}/${this.payForm.postalCode}`
      })
        .then(response => {
          this.payForm.city = response.data.city
          this.loadingCity = false
        })
        .catch(e => {
          this.loadingCity = false
        })
    },
    submit() {
      //We have a bug where users didnt get a reset state after finishing an order
      //This ensures that the state resets when they pay
      if (this.orderActivated || this.paymentStatus === 'success') {
        this.$store.commit('letter/pay/RESET_PAY_STATES')
      }
      this.payForm.mobile = this.formatPhoneNumber()
      this.SAVE(this.payForm)

      this.$router.push({ name: 'approveStatus' })
    }
  }
}
</script>

<style lang="scss" scoped>
.approve-contact-page {
  padding: 3em;
}

.form-container {
  margin-top: 2.4rem;
}

.form-container > * {
  margin-bottom: 1rem;
  width: 100%;
}

.form-2-col {
  width: 100%;
  display: inline-flex;
  justify-items: center;

  > * {
    width: 100%;
  }

  .country-code {
    width: 40%;
    padding-right: 1em;
    padding-top: 1em;
  }
}

.store-option {
  padding: 1.5rem 0rem;

  .store-checkbox {
    margin-bottom: 0.5em;
  }
}

.no-border-checkbox {
  padding: 1rem 0rem;
}

.custom-validation-text {
  font-size: 0.875em;
  font-weight: 300;
  color: $warning;
}

@media screen and (max-width: 72rem) {
  .approve-contact-page {
    padding: 0;
  }

  .form-2-col {
    grid-template-columns: repeat(1, minmax(22rem, 2fr));
  }
}

.read-only {
  pointer-events: none;
}

.read-only-notice {
  margin-top: 1rem;
  padding: 0 1rem;
  border-radius: 25px;
  background-color: red;
  color: white;
  width: fit-content;
}
</style>
